import React, { useEffect, useRef } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { setAccessToken } from '../../redux/auth-slice'
import { useAppThunkDispatch } from '../../redux/store'
import '../../../node_modules/@okta/okta-signin-widget/dist/css/okta-sign-in.min.css'
import { useAuth } from 'oidc-react'
import { setLoggedInUser } from '../../redux/common-data-slice'
import { Box, LinearProgress } from '@mui/material'

interface IProps extends RouteComponentProps {
  history: any
  location: any
  match: any
  config: any
}

const LoginOIDCCodeComponent: React.FC<IProps> = ({ history }) => {
  const auth = useAuth() as any
  const dispatch = useAppThunkDispatch()
  const hasProcessedAuth = useRef(false)

  useEffect(() => {
    // Only process once to avoid duplicate token storage and redirects
    if (hasProcessedAuth.current) {
      return
    }

    // Check if we already have a valid token in storage
    const existingToken = localStorage.getItem('access_token')
    const tokenExpiry = window.sessionStorage.getItem('tokenExpiry')
    const sessionUserInfo = window.sessionStorage.getItem('sessionUserInfo')

    // If valid token exists, just redirect without processing again
    if (existingToken && tokenExpiry && sessionUserInfo) {
      const currentTime = new Date().getTime()
      const expiryTime = parseInt(tokenExpiry)

      if (currentTime < expiryTime) {
        hasProcessedAuth.current = true

        // Get redirect path and clear it
        const redirectPath = sessionStorage.getItem('oidcRedirectPath') || '/'
        sessionStorage.removeItem('oidcRedirectPath')

        // Redirect immediately
        window.location.href = redirectPath
        return
      }
    }

    // No code in URL and auth is done loading = post-logout redirect (not a real callback).
    // Send the user to /login so they can sign in normally.
    if (!auth?.isLoading) {
      const urlParams = new URLSearchParams(window.location.search)
      if (!urlParams.get('code') && !auth?.userData?.access_token) {
        hasProcessedAuth.current = true
        window.location.href = '/login'
        return
      }
    }

    // Process new authentication
    if (auth?.userData?.access_token && !hasProcessedAuth.current) {
      hasProcessedAuth.current = true

      try {
        let userData = auth.userData?.profile || ({} as any)
        userData = { ...userData, sub: userData.oid }
        const token = auth.userData.access_token

        // Clear any existing session data
        window.sessionStorage.removeItem('encryptedToken')
        window.sessionStorage.removeItem('encryptedTokenTS')
        window.sessionStorage.removeItem('sessionUserInfo')
        window.sessionStorage.removeItem('tokenExpiry')
        localStorage.removeItem('access_token')

        // Store tokens
        localStorage.setItem('access_token', token)

        // Store timestamps
        const tokenTimestamp = new Date().getTime().toString()
        const expiryTimestamp = (new Date().getTime() + 3600000).toString()
        window.sessionStorage.setItem('encryptedTokenTS', tokenTimestamp)
        window.sessionStorage.setItem('tokenExpiry', expiryTimestamp)

        // Store user info
        window.sessionStorage.setItem(
          'sessionUserInfo',
          JSON.stringify(userData)
        )

        // Clear the intentional logout flag so OIDCAuthWrapper re-initializes normally
        sessionStorage.removeItem('userLoggedOut')

        // Update Redux
        dispatch(
          setAccessToken({
            loading: false,
            accessToken: { value: token },
            user: userData,
          })
        )
        dispatch(setLoggedInUser(userData))

        // Get redirect path
        const redirectPath = sessionStorage.getItem('oidcRedirectPath') || '/'
        sessionStorage.removeItem('oidcRedirectPath')

        // Single redirect with small delay to ensure storage completes
        setTimeout(() => {
          window.location.href = redirectPath
        }, 500)
      } catch (error) {
        console.error('Error processing authentication:', error)
        hasProcessedAuth.current = false
      }
    }
  }, [auth?.userData?.access_token, auth?.isLoading, dispatch])

  // Loading state
  if (auth?.isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    )
  }

  // Success state
  if (auth?.userData?.access_token) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>
          <div>Welcome back {auth.userData?.profile?.name}!</div>
          <div style={{ fontSize: '0.9rem', marginTop: '10px', color: '#666' }}>
            Redirecting...
          </div>
          <Box sx={{ width: '200px', marginTop: '20px' }}>
            <LinearProgress />
          </Box>
        </div>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <div>
        <div>Processing authentication...</div>
        <Box sx={{ width: '100%', marginTop: '20px' }}>
          <LinearProgress />
        </Box>
      </div>
    </Box>
  )
}

export default LoginOIDCCodeComponent
